import React, { useMemo } from 'react';
import { AddressReportResponse } from '../../services/addressManagementService';
import ComparisonMap, { PodMarker } from './ComparisonMap';
import './AddressReportBeta.css';
import gmapsIcon from '../../assets/maps/gmaps.svg';
import radarIcon from '../../assets/maps/radar.svg';
import uberIcon from '../../assets/maps/uber.svg';

interface AddressReportBetaProps {
    data: AddressReportResponse | null;
    isLoading: boolean;
    podMarkers?: PodMarker[];
}

const AddressReportBeta: React.FC<AddressReportBetaProps> = ({ data, isLoading, podMarkers = [] }) => {
    // Parser helpers
    const parsedGoogleResponse = useMemo(() => {
        if (!data?.addressValidationInfo?.googleResponse) return null;
        try {
            return JSON.parse(data.addressValidationInfo.googleResponse);
        } catch (e) {
            console.error("Failed to parse googleResponse", e);
            return null;
        }
    }, [data]);

    const parsedRadarResponse = useMemo(() => {
        if (!data?.addressValidationInfo?.radarResponse) return null;
        try {
            return JSON.parse(data.addressValidationInfo.radarResponse);
        } catch (e) {
            console.error("Failed to parse radarResponse", e);
            return null;
        }
    }, [data]);

    const parsedUberResponse = useMemo(() => {
        if (!data?.addressValidationInfo?.uberResponse) return null;
        try {
            return JSON.parse(data.addressValidationInfo.uberResponse);
        } catch (e) {
            console.error("Failed to parse uberResponse", e);
            return null;
        }
    }, [data]);

    const parsedDestinationsResponse = useMemo(() => {
        if (!data?.addressValidationInfo?.googleGeocodeDestinationsResponse) return null;
        try {
            return JSON.parse(data.addressValidationInfo.googleGeocodeDestinationsResponse);
        } catch (e) {
            console.error("Failed to parse destinationsResponse", e);
            return null;
        }
    }, [data]);

    // Calculate Delta (Haversine formula)
    const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number) => {
        const R = 6371e3; // metres
        const φ1 = lat1 * Math.PI / 180;
        const φ2 = lat2 * Math.PI / 180;
        const Δφ = (lat2 - lat1) * Math.PI / 180;
        const Δλ = (lon2 - lon1) * Math.PI / 180;

        const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c;
    };

    const deltaInfo = useMemo(() => {
        if (data?.address?.latitude && data?.address?.longitude && data?.address?.radarLatitude && data?.address?.radarLongitude) {
            const distMeters = calculateDistance(data.address.latitude, data.address.longitude, data.address.radarLatitude, data.address.radarLongitude);
            const distFeet = distMeters * 3.28084;
            const dLat = Math.abs(data.address.latitude - data.address.radarLatitude);
            const dLon = Math.abs(data.address.longitude - data.address.radarLongitude);
            return {
                meters: distMeters.toFixed(2),
                feet: distFeet.toFixed(2),
                dLat: dLat.toFixed(7),
                dLon: dLon.toFixed(7)
            };
        }
        return null;
    }, [data]);

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Generating Report...</p>
            </div>
        );
    }

    if (!data) return <div className="p-4 text-center text-muted">No report data available.</div>;

    const { address } = data;
    const uspsData = parsedGoogleResponse?.Result?.UspsData;
    const googleComponents = parsedGoogleResponse?.Result?.Address?.AddressComponents;
    const radarData = parsedRadarResponse?.RadarValidateResponse?.Address;
    const navPoint = parsedDestinationsResponse?.destinations?.[0]?.navigationPoints?.[0];

    // Helper to find google address component
    const getGoogleComponent = (type: string) => {
        return googleComponents?.find((c: any) => c.ComponentType === type)?.ComponentName?.Text;
    };

    const zipSuffix = address.zipSuffix || getGoogleComponent('postal_code_suffix');
    const fullZip = zipSuffix ? `${address.zip}-${zipSuffix}` : address.zip;

    const mapZoom = address.geoFenceConfidence === 'HIGH' ? 20 : 19;

    return (
        <div className="address-report-beta">
            <div className="report-page">
                {/* TOP BAR */}
                <div className="topbar">
                    <div className="topbar-left">
                        <div className="logo-mark">BetterTrucks</div>
                        <div className="logo-sep"></div>
                        <div className="topbar-label">Address Validation Report</div>
                    </div>
                    <div className="topbar-right">
                        {new Date().toLocaleDateString()} · {new Date().toLocaleTimeString()} UTC<br />
                        ID #{address.id} · Source: {address.validationSource || 'N/A'}
                    </div>
                </div>

                {/* HERO */}
                <div className="hero">
                    <div>
                        <div className="hero-address">
                            {address.street1}<br />
                            {address.street2 && <>{address.street2}<br /></>}
                            {address.city}, {address.stateCode} {fullZip}
                        </div>
                        <div className="hero-sub">
                            {uspsData?.County?.toLowerCase().replace(/\b\w/g, (l: string) => l.toUpperCase())} County · {address.country} · Trace: {address.traceId}
                        </div>
                    </div>
                    <div className="hero-badges">
                        {address.isValid && (
                            <span className="chip chip-valid">
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '4px', display: 'inline-block', verticalAlign: 'middle' }}>
                                    <circle cx="7" cy="7" r="7" fill="#1E7E34"/>
                                    <path d="M4 7L6 9L10 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                Valid & Deliverable
                            </span>
                        )}
                        <span className="chip chip-blue">
                            <img src={gmapsIcon} alt="Google" style={{ width: '14px', height: '14px', marginRight: '4px', display: 'inline-block', verticalAlign: 'middle' }} />
                            Google: {address.googlePossibleNextAction || 'N/A'}
                        </span>
                        <span className="chip chip-teal">
                            <img src={radarIcon} alt="Radar" style={{ width: '14px', height: '14px', marginRight: '4px', display: 'inline-block', verticalAlign: 'middle' }} />
                            Radar: {address.isRadarValid ? 'Verified' : 'Unverified'}
                        </span>
                        <span className="chip chip-uber">
                            <img src={uberIcon} alt="Uber" style={{ width: '14px', height: '14px', marginRight: '4px', display: 'inline-block', verticalAlign: 'middle' }} />
                            Uber: {address.isUberValid ? 'Verified' : 'Unverified'}
                        </span>
                    </div>
                </div>

                {/* BODY */}
                <div className="body">
                    {/* COORDINATES */}
                    <div className="section-head">Geocoordinates</div>
                    <div className="coord-grid mb">
                        {/* Google */}
                        <div className="coord-cell">
                            <div className="coord-cell-head">
                                <img src={gmapsIcon} alt="Google" style={{ width: '16px', height: '16px', marginRight: '6px', display: 'inline-block', verticalAlign: 'middle' }} />
                                Google Validation API
                            </div>
                            <div className="coord-pair-row">
                                <div className="coord-field">
                                    <label>Latitude</label>
                                    <div className="val">{address.latitude}</div>
                                </div>
                                <div className="coord-field">
                                    <label>Longitude</label>
                                    <div className="val">{address.longitude}</div>
                                </div>
                            </div>
                            <div className="coord-meta">
                                {parsedGoogleResponse?.Result?.Geocode?.LocationType && (
                                    <span style={{ background: 'var(--accent-bg)', color: 'var(--google)', padding: '.1rem .4rem', fontSize: '.62rem', fontFamily: "'DM Mono',monospace", borderRadius: '2px' }}>
                                        {parsedGoogleResponse.Result.Geocode.LocationType}
                                    </span>
                                )}
                                Granularity: {parsedGoogleResponse?.Result?.Verdict?.GeocodeGranularity || 'N/A'}
                            </div>
                            <a className="maps-link" href={`https://maps.google.com/?q=${address.latitude},${address.longitude}`} target="_blank" rel="noreferrer">
                                ↗ Open in Google Maps
                            </a>
                        </div>

                        {/* Radar */}
                        <div className="coord-cell">
                            <div className="coord-cell-head">
                                <img src={radarIcon} alt="Radar" style={{ width: '16px', height: '16px', marginRight: '6px', display: 'inline-block', verticalAlign: 'middle' }} />
                                Radar Forward Geocode
                            </div>
                            <div className="coord-pair-row">
                                <div className="coord-field">
                                    <label>Latitude</label>
                                    <div className="val">{address.radarLatitude}</div>
                                </div>
                                <div className="coord-field">
                                    <label>Longitude</label>
                                    <div className="val">{address.radarLongitude}</div>
                                </div>
                            </div>
                            <div className="coord-meta">
                                {parsedRadarResponse?.RadarForwardResponse?.Addresses?.[0]?.Confidence && (
                                    <span style={{ background: '#E6F4F2', color: 'var(--radar)', padding: '.1rem .4rem', fontSize: '.62rem', fontFamily: "'DM Mono',monospace", borderRadius: '2px' }}>
                                        {parsedRadarResponse.RadarForwardResponse.Addresses[0].Confidence}
                                    </span>
                                )}
                                Confidence: {parsedRadarResponse?.RadarForwardResponse?.Addresses?.[0]?.Confidence || 'N/A'}
                            </div>
                            <a className="maps-link" style={{ color: 'var(--radar)' }} href={`https://maps.google.com/?q=${address.radarLatitude},${address.radarLongitude}`} target="_blank" rel="noreferrer">
                                ↗ Open in Google Maps
                            </a>
                        </div>

                        {/* Uber */}
                        <div className="coord-cell">
                            <div className="coord-cell-head">
                                <img src={uberIcon} alt="Uber" style={{ width: '16px', height: '16px', marginRight: '6px', display: 'inline-block', verticalAlign: 'middle' }} />
                                Uber Geocode API
                            </div>
                            <div className="coord-pair-row">
                                <div className="coord-field">
                                    <label>Latitude</label>
                                    <div className="val">{address.uberLatitude ?? 'N/A'}</div>
                                </div>
                                <div className="coord-field">
                                    <label>Longitude</label>
                                    <div className="val">{address.uberLongitude ?? 'N/A'}</div>
                                </div>
                            </div>
                            <div className="coord-meta">
                                {parsedUberResponse?.Entity?.Location?.Categories?.length > 0 && (
                                    <span style={{ background: '#EDEDED', color: '#000000', padding: '.1rem .4rem', fontSize: '.62rem', fontFamily: "'DM Mono',monospace", borderRadius: '2px' }}>
                                        {parsedUberResponse.Entity.Location.Categories.join(' · ')}
                                    </span>
                                )}
                                Matched: {parsedUberResponse?.Entity?.Location?.Address || 'N/A'}
                            </div>
                            {address.uberLatitude && address.uberLongitude && (
                                <a className="maps-link" style={{ color: '#000000' }} href={`https://maps.google.com/?q=${address.uberLatitude},${address.uberLongitude}`} target="_blank" rel="noreferrer">
                                    ↗ Open in Google Maps
                                </a>
                            )}
                        </div>

                    </div>

                    {/* Delta */}
                    {deltaInfo && (
                        <div className="delta-strip mb">
                            <div className="delta-strip-block delta-strip-primary">
                                <div className="delta-label">Coordinate Delta · Google ↔ Radar</div>
                                <div className="delta-strip-primary-value">
                                    <span className="delta-number">{deltaInfo.meters}<span className="delta-unit">m</span></span>
                                    <span className="delta-feet">≈ {deltaInfo.feet} feet</span>
                                </div>
                            </div>
                            <div className="delta-strip-divider"></div>
                            <div className="delta-strip-block">
                                <div className="delta-label">Δ Latitude</div>
                                <div className="delta-strip-value">{deltaInfo.dLat}°</div>
                            </div>
                            <div className="delta-strip-divider"></div>
                            <div className="delta-strip-block">
                                <div className="delta-label">Δ Longitude</div>
                                <div className="delta-strip-value">{deltaInfo.dLon}°</div>
                            </div>
                            <div className="delta-strip-divider"></div>
                            <div className="delta-strip-block">
                                <div className="delta-label">Source Used</div>
                                <div className="delta-strip-value">{address.coordinateSource || 'N/A'}</div>
                            </div>
                        </div>
                    )}

                    <div className="section-head">Map Preview — Google coordinate (zoom {mapZoom})</div>
                    <div className="map-wrap mb p-0 overflow-hidden" style={{ height: '400px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                        <div className="map-bar" style={{ borderBottom: '1px solid #eee', padding: '8px 12px' }}>
                            <div className="map-legend" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                <img
                                    src={gmapsIcon}
                                    alt="Google Maps"
                                    style={{ width: '20px', height: '20px' }}
                                />
                                <span>
                                    <strong>Google:</strong> {address.latitude ? address.latitude.toFixed(6) : 'N/A'}, {address.longitude ? address.longitude.toFixed(6) : 'N/A'}
                                </span>
                            </div>
                            {deltaInfo && (
                                <div className="map-legend" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                    <img
                                        src={radarIcon}
                                        alt="Radar"
                                        style={{ width: '20px', height: '20px' }}
                                    />
                                    <span>
                                        <strong>Radar:</strong> {address.radarLatitude ? address.radarLatitude.toFixed(6) : 'N/A'}, {address.radarLongitude ? address.radarLongitude.toFixed(6) : 'N/A'} &nbsp;·&nbsp; {deltaInfo.meters}m apart
                                    </span>
                                </div>
                            )}
                            {address.uberLatitude && address.uberLongitude && (
                                <div className="map-legend" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: podMarkers.length > 0 ? '4px' : 0 }}>
                                    <img
                                        src={uberIcon}
                                        alt="Uber"
                                        style={{ width: '20px', height: '20px' }}
                                    />
                                    <span>
                                        <strong>Uber:</strong> {address.uberLatitude.toFixed(6)}, {address.uberLongitude.toFixed(6)}
                                    </span>
                                </div>
                            )}
                            {podMarkers.length > 0 && (
                                <div className="map-legend" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{
                                        width: '12px',
                                        height: '12px',
                                        borderRadius: '50%',
                                        backgroundColor: '#6f42c1',
                                        display: 'inline-block'
                                    }}></span>
                                    <span>
                                        <strong>PODs:</strong> {podMarkers.length} location{podMarkers.length === 1 ? '' : 's'}
                                    </span>
                                </div>
                            )}
                        </div>
                        <ComparisonMap
                            googleCoordinates={
                                address.latitude && address.longitude
                                    ? { lat: address.latitude, lng: address.longitude }
                                    : undefined
                            }
                            radarCoordinates={
                                address.radarLatitude && address.radarLongitude
                                    ? { lat: address.radarLatitude, lng: address.radarLongitude }
                                    : undefined
                            }
                            uberCoordinates={
                                address.uberLatitude && address.uberLongitude
                                    ? { lat: address.uberLatitude, lng: address.uberLongitude }
                                    : undefined
                            }
                            podMarkers={podMarkers}
                            address={`${address.street1}, ${address.city}, ${address.stateCode} ${address.zip}`}
                            height="360px"
                            initialMapType="satellite"
                            hideControls={true}
                            zoom={mapZoom}
                        />
                    </div>

                    {/* VALIDATION RESULTS */}
                    <div className="section-head">Validation Results</div>
                    <div className="val-grid val-grid-3 mb">
                        <div className="val-panel">
                            <div className="val-panel-head">
                                <img src={gmapsIcon} alt="Google" style={{ width: '16px', height: '16px', marginRight: '6px', display: 'inline-block', verticalAlign: 'middle' }} />
                                Google Validation API
                            </div>
                            <table className="kv">
                                <tbody>
                                    <tr><td>Status</td><td><span className={`tag ${address.isGoogleValid ? 'tag-green' : 'tag-orange'}`}>{address.isGoogleValid ? 'VALID' : 'INVALID'}</span></td></tr>
                                    <tr><td>Next Action</td><td><span className="tag tag-green">{address.googlePossibleNextAction || 'N/A'}</span></td></tr>
                                    <tr><td>Granularity</td><td>{parsedGoogleResponse?.Result?.Verdict?.ValidationGranularity || 'N/A'}</td></tr>
                                    <tr><td>Inferred Components</td><td>{parsedGoogleResponse?.Result?.Verdict?.HasInferredComponents ? 'Yes' : 'No'}</td></tr>
                                    <tr><td>Place ID</td><td style={{ fontSize: '.6rem' }}>{address.googlePlaceId || 'N/A'}</td></tr>
                                    {/* <tr><td>Place Types</td><td style={{ fontSize: '.6rem' }}>{address.googlePlaceTypes ? (() => { try { const arr = JSON.parse(address.googlePlaceTypes!); return Array.isArray(arr) ? arr.join(', ') : address.googlePlaceTypes; } catch { return address.googlePlaceTypes; } })() : 'N/A'}</td></tr> */}
                                    <tr><td>Formatted Address</td><td style={{ fontSize: '.6rem' }}>{address.googleFormattedAddress || 'N/A'}</td></tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="val-panel">
                            <div className="val-panel-head">
                                <img src={radarIcon} alt="Radar" style={{ width: '16px', height: '16px', marginRight: '6px', display: 'inline-block', verticalAlign: 'middle' }} />
                                Radar Validation API
                            </div>
                            <table className="kv">
                                <tbody>
                                    <tr><td>Status</td><td><span className={`tag ${address.isRadarValid ? 'tag-teal' : 'tag-orange'}`}>{address.isRadarValid ? 'VERIFIED' : 'UNVERIFIED'}</span></td></tr>
                                    <tr><td>Score</td><td>{address.radarScore ?? 'N/A'} / 100</td></tr>
                                    <tr><td>Confidence</td><td>{parsedRadarResponse?.RadarForwardResponse?.Addresses?.[0]?.Confidence || 'N/A'}</td></tr>
                                    <tr><td>Property Type</td><td>{radarData?.Metadata?.PropertyType || 'N/A'}</td></tr>
                                    <tr><td>Plus4</td><td>{address.zipSuffix || radarData?.Plus4 || 'N/A'}</td></tr>
                                    <tr><td>Place ID</td><td style={{ fontSize: '.6rem', wordBreak: 'break-all' }}>{address.radarPlaceId || 'N/A'}</td></tr>
                                    <tr><td>Formatted Address</td><td style={{ fontSize: '.6rem' }}>{address.radarFormattedAddress || 'N/A'}</td></tr>
                                </tbody>
                            </table>
                        </div>
                        <div className="val-panel">
                            <div className="val-panel-head">
                                <img src={uberIcon} alt="Uber" style={{ width: '16px', height: '16px', marginRight: '6px', display: 'inline-block', verticalAlign: 'middle' }} />
                                Uber Geocode API
                            </div>
                            <table className="kv">
                                <tbody>
                                    <tr><td>Status</td><td><span className={`tag ${address.isUberValid ? 'tag-green' : 'tag-orange'}`}>{address.isUberValid ? 'VERIFIED' : 'UNVERIFIED'}</span></td></tr>
                                    <tr><td>Coordinates</td><td>{address.uberLatitude && address.uberLongitude ? `${address.uberLatitude}, ${address.uberLongitude}` : 'N/A'}</td></tr>
                                    <tr><td>Categories</td><td>{parsedUberResponse?.Entity?.Location?.Categories?.join(' · ') || 'N/A'}</td></tr>
                                    <tr><td>Place ID</td><td style={{ fontSize: '.6rem', wordBreak: 'break-all' }}>{address.uberPlaceId || 'N/A'}</td></tr>
                                    <tr><td>Matched Address</td><td style={{ fontSize: '.6rem' }}>{parsedUberResponse?.Entity?.Location?.Address || 'N/A'}</td></tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* USPS + LOGISTICS */}
                    <div className="section-head">USPS & Logistics Intelligence</div>
                    <div className="bottom-grid">
                        <div className="panel">
                            <div className="panel-head">USPS DPV Flags</div>
                            <div className="panel-body">
                                <table className="kv">
                                    <tbody>
                                        <tr><td>DPV Confirmation</td><td><span className="tag tag-green">{uspsData?.DpvConfirmation === 'Y' ? 'Y — Deliverable' : uspsData?.DpvConfirmation}</span></td></tr>
                                        <tr><td>Vacant</td><td><span className={`tag ${uspsData?.DpvVacant === 'Y' ? 'tag-orange' : 'tag-green'}`}>{uspsData?.DpvVacant === 'Y' ? 'Y — Vacant' : 'N — Occupied'}</span></td></tr>
                                        <tr><td>CMRA / P.O. Box</td><td><span className="tag tag-green">{uspsData?.DpvCmra === 'Y' ? 'Y' : 'N'}</span></td></tr>
                                        <tr><td>No-Stat</td><td><span className="tag tag-green">{uspsData?.DpvNoStat === 'Y' ? 'Y' : 'N'}</span></td></tr>
                                        <tr><td>Enhanced Delivery</td><td><span className="tag tag-green">{uspsData?.DpvEnhancedDeliveryCode || 'N/A'}</span></td></tr>
                                        <tr><td>Door Not Accessible</td><td><span className={`tag ${uspsData?.DpvDoorNotAccessible === 'Y' ? 'tag-orange' : 'tag-green'}`}>{uspsData?.DpvDoorNotAccessible === 'Y' ? 'Y' : 'N'}</span></td></tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="panel">
                            <div className="panel-head">Logistics Details</div>
                            <div className="panel-body">
                                <table className="kv">
                                    <tbody>
                                        <tr><td>Carrier Route</td><td>{uspsData?.CarrierRoute || 'N/A'}</td></tr>
                                        <tr><td>Route Type</td><td>{uspsData?.CarrierRouteIndicator || 'N/A'}</td></tr>
                                        <tr><td>Record Type</td><td>{uspsData?.AddressRecordType || 'N/A'}</td></tr>
                                        <tr><td>County</td><td>{uspsData?.County || 'N/A'}</td></tr>
                                        <tr><td>Is Apartment</td><td><span className={`tag ${address.isApartment ? 'tag-orange' : 'tag-green'}`}>{address.isApartment ? 'Yes' : 'No'}</span></td></tr>
                                        <tr><td>GeoFence Confidence</td><td><span className="tag tag-blue">{address.geoFenceConfidence || 'N/A'}</span></td></tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* NAV POINT */}
                    <div className="section-head">Navigation Access Point — Google Destinations API</div>
                    <div className="nav-panel mb">
                        <div className="panel-head">Driveable / Walkable Entry</div>
                        <div className="nav-inner">
                            <div className="nav-field">
                                <label>Latitude</label>
                                <div className="val">{navPoint?.location?.latitude || 'N/A'}</div>
                            </div>
                            <div className="nav-field">
                                <label>Longitude</label>
                                <div className="val">{navPoint?.location?.longitude || 'N/A'}</div>
                            </div>
                            <div className="nav-field">
                                <label>Street</label>
                                <div className="val">{navPoint?.displayName?.text || 'N/A'}</div>
                            </div>
                            <div className="nav-field">
                                <label>Travel Modes</label>
                                <div className="val">{navPoint?.travelModes?.join(' · ') || 'N/A'}</div>
                            </div>
                        </div>
                    </div>

                </div>

                {/* FOOTER */}
                <div className="footer">
                    <div className="footer-left">
                        BetterTrucks CheckPoint Engine · Address ID #{address.id}<br />
                        Trace: {address.traceId}
                    </div>
                    <div className="footer-right">
                        Created: {new Date(address.createdAt).toLocaleString()}<br />
                        Validation Source: {address.validationSource}
                    </div>
                </div>
            </div>

            <button className="fab no-print" onClick={() => window.print()}>⎙ PRINT / PDF</button>
        </div>
    );
};

export default AddressReportBeta;
